import React, {useEffect, useRef, useState} from "react";
import styles from "./InviteRemoveUsers.module.css";
import InviteNewUser from "./InviteNewUser/InviteNewUser";
import TabbedView from "../TabbedView/TabbedView";
import { ITab } from "../../../Types/ITab";
import { getRolesOptions } from "../../../Data/RolesData";
import { FloatingLabelSelectOption } from "../../../Types/Form/FloatingLabelSelectOption";
import InviteExistingUser from "./InviteExistingUser/InviteExistingUser";
import RemoveUser from "./RemoveUser/RemoveUser";
import { axiosInstance } from "../../../Authentication/AxiosInterceptor";
import { InviteUserDisplayInfo } from "../../../Types/InviteRemoveUser/InviteUserDisplayInfo";
import {ClientUserDisplayInfo, ClientUserDisplayInfoWithRoles} from "../../../Types/ClientUserDisplayInfo";
import {useParams} from "react-router-dom";
import { useAppSelector } from "../../../Redux/Store/Store";
import { getClient } from "../../../Redux/Store/Slices/client";
import LoadingIcon from "../LoadingIcon/LoadingIcon";
import { ProductType } from "../../../Types/ProductType";
import { selectRolePermissions } from "../../../Redux/Store/Slices/questionnaire";
import { RolePermission } from "../../../Types/Roles/RolePermission";

type InviteRemoveUsersProps = {
  areaId?: string;
  productType?: ProductType;
  /** When set (e.g. Improve/Track), permissions come from the area API instead of questionnaire Redux. */
  rolePermissions?: RolePermission;
  /** Called after a user is invited or removed so parents can refresh area-scoped data (e.g. assignable users). */
  onAreaUsersChanged?: () => void;
  /** Heading on the View Users tab. Diagnostics keep the default. */
  usersHeading?: string;
  /** Fixed role list. When set, product role lookup is skipped. */
  rolesOverride?: FloatingLabelSelectOption[];
  /** When set, replaces the diagnostic permission check. */
  canManageUsersOverride?: boolean;
  /** Portfolio and other non-diagnostic hosts supply their own directory. */
  loadUsers?: () => Promise<ClientUserDisplayInfo[]>;
  loadInvitableUsers?: () => Promise<InviteUserDisplayInfo[]>;
  inviteExisting?: (user: InviteUserDisplayInfo, role: string) => Promise<void>;
  inviteNew?: (givenName: string, familyName: string, email: string, role: string) => Promise<void>;
  removeUser?: (user: ClientUserDisplayInfo) => Promise<void>;
  /** Used in the remove confirmation, for example "portfolio". */
  membershipLabel?: string;
  /** Shown on Invite and Remove when the current user cannot manage membership. */
  permissionMessage?: string;
  /** Shown on Remove Users, for example when a global portfolio grants reader access to the whole client. */
  removeUsersNote?: string;
  /** Reloads the directory when this value changes, for example after ownership moves. */
  usersRefreshKey?: string;
  /** Extra content at the top of the View Users tab. Diagnostics leave this unset. */
  viewUsersExtra?: React.ReactNode;
  /** When set, all three tabs show this message instead of their contents. */
  membershipRestrictedMessage?: string;
  /** Shown above the existing-user picker to explain who is listed. */
  inviteScopeNote?: string;
};

const InviteRemoveUsers = ({
  areaId: propAreaId,
  productType,
  rolePermissions: rolePermissionsProp,
  onAreaUsersChanged,
  usersHeading = "Diagnostic Users",
  rolesOverride,
  canManageUsersOverride,
  loadUsers,
  loadInvitableUsers,
  inviteExisting,
  inviteNew,
  removeUser,
  membershipLabel = "area",
  permissionMessage,
  removeUsersNote,
  usersRefreshKey,
  viewUsersExtra,
  membershipRestrictedMessage,
  inviteScopeNote,
}: InviteRemoveUsersProps = {}) => {
  const tabs: ITab[] = [
    {
      title: "View Users",
      active: true,
      url: "",
    },
    {
      title: "Invite Users",
      active: false,
      url: "",
    },
    {
      title: "Remove Users",
      active: false,
      url: "",
    },
  ];

  const [roles, setRoles] = React.useState<FloatingLabelSelectOption[]>([]);

  const [activeTab, setActiveTab] = React.useState<ITab>(tabs[0]);

  const {areaId: paramAreaId} = useParams();
  const areaId = propAreaId || paramAreaId;

  const client = useAppSelector(getClient)
  const rolePermissionsFromStore = useAppSelector(selectRolePermissions());
  const rolePermissions =
    productType === ProductType.Improve
      ? rolePermissionsProp
      : rolePermissionsProp ?? rolePermissionsFromStore;
  const canManageUsers = canManageUsersOverride ?? (
    !!rolePermissions?.inviteRemoveParticipant ||
    !!rolePermissions?.inviteRemoveReader ||
    !!rolePermissions?.inviteRemoveLeader
  );
  const [directoryVersion, setDirectoryVersion] = useState(0);
  const loadUsersRef = useRef(loadUsers);
  const loadInvitableRef = useRef(loadInvitableUsers);
  loadUsersRef.current = loadUsers;
  loadInvitableRef.current = loadInvitableUsers;

  const [clientUsersList, setClientUsersList] = React.useState<
    FloatingLabelSelectOption[]
  >([]);
  const [clientUsersInfoMap, setClientUsersInfoMap] = React.useState<Map<string, InviteUserDisplayInfo>>(new Map());

  const [areaUsersList, setAreaUsersList] = useState<ClientUserDisplayInfo[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

  const getClientUsersList = async () => {
    try {
      if (!areaId || areaId === 'undefined') {
        return;
      }
      const response = await axiosInstance.get<InviteUserDisplayInfo[]>(
        `GetEvaluateAreaInvitableUsers/${areaId}`
      );

      const uniqueUsers = new Map<string, InviteUserDisplayInfo>();
      response.data.forEach((user) => {
        if (!uniqueUsers.has(user.sub)) {
          uniqueUsers.set(user.sub, user);
        }
      });

      const options: FloatingLabelSelectOption[] = Array.from(uniqueUsers.values()).map(
        (user) => ({
          value: user.sub,
          label: `${user.fullName} (${user.email})`,
        })
      );

      const userMap = new Map<string, InviteUserDisplayInfo>();
      uniqueUsers.forEach((user, sub) => {
        userMap.set(sub, user);
      });
      setClientUsersInfoMap(userMap);

      setClientUsersList(options);
    } catch (error) {
      console.error("Error fetching client users list:", error);
    }
  };

  const getEvaluateUserInfoByAreaId = async (areaId: string) => {
    try {
      setLoadingUsers(true);
      const response = await axiosInstance.get<ClientUserDisplayInfo[]>(
          `GetAreaUserDisplayInfos/${areaId}`
      );
      setAreaUsersList(response.data);
    } catch (error) {
      console.error("Error fetching area users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    const getRoles = async () => {
      try {
        if(client && client.id && productType !== undefined) {
          const roleData = await getRolesOptions(
            client.id,
            productType
          );
          setRoles(roleData);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    if (rolesOverride) {
      setRoles(rolesOverride);
    } else {
      getRoles();
    }
  }, [areaId, client, productType, rolesOverride]);

  useEffect(() => {
    if (membershipRestrictedMessage) return;
    const load = loadUsersRef.current;
    if (load) {
      let cancelled = false;
      setLoadingUsers(true);
      load()
        .then((users) => {
          if (!cancelled) setAreaUsersList(users);
        })
        .catch((error) => console.error("Error fetching users:", error))
        .finally(() => {
          if (!cancelled) setLoadingUsers(false);
        });
      return () => {
        cancelled = true;
      };
    }
    if (areaId && areaId !== "undefined") {
      getEvaluateUserInfoByAreaId(areaId);
    }
  }, [areaId, directoryVersion, usersRefreshKey, membershipRestrictedMessage]);

  useEffect(() => {
    if (membershipRestrictedMessage || !canManageUsers || activeTab.title !== "Invite Users") return;
    const load = loadInvitableRef.current;
    if (load) {
      let cancelled = false;
      load()
        .then((users) => {
          if (cancelled) return;
          const options: FloatingLabelSelectOption[] = users.map((user) => ({
            value: user.sub,
            label: `${user.fullName} (${user.email})`,
          }));
          const userMap = new Map<string, InviteUserDisplayInfo>();
          users.forEach((user) => userMap.set(user.sub, user));
          setClientUsersList(options);
          setClientUsersInfoMap(userMap);
        })
        .catch((error) => console.error("Error fetching invitable users:", error));
      return () => {
        cancelled = true;
      };
    }
    if (areaId && areaId !== "undefined") {
      getClientUsersList();
    }
  }, [activeTab.title, areaId, canManageUsers, directoryVersion, membershipRestrictedMessage]);

  const handleTabChanged = (tab: ITab) => {
    setActiveTab(tab);
  };

  const handleAddUser = (_user: InviteUserDisplayInfo) => {
    if (loadUsersRef.current) {
      setDirectoryVersion((version) => version + 1);
    } else if (areaId && areaId !== "undefined") {
      getEvaluateUserInfoByAreaId(areaId);
    }
    onAreaUsersChanged?.();
  };

  const handleRemoveUserFromList = (user: ClientUserDisplayInfoWithRoles) => {
    setAreaUsersList((prev) => prev.filter((u) => u.sub !== user.sub));
    onAreaUsersChanged?.();
  };

  return (
    <div className={styles.container}>
      <TabbedView
        data={tabs}
        activeTab={activeTab}
        activeTabChanged={handleTabChanged}
        disableNavigation
      />

      {membershipRestrictedMessage ? (
        <div className={styles.permissionMessage}>{membershipRestrictedMessage}</div>
      ) : activeTab.title === "View Users" ? (
        <div className={styles.content}>
          <div className={styles.viewUsersContainer}>
            <h3>{usersHeading}</h3>
            <div className={styles.usersList}>
              {loadingUsers ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                  <LoadingIcon />
                </div>
              ) : areaUsersList.length > 0 ? (
                areaUsersList.map((user) => (
                  <div key={user.sub} className={styles.userItem}>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>{user.fullName}</div>
                      <div className={styles.userEmail}>{user.email}</div>
                    </div>
                    <div className={styles.userRole}>
                       {user.roles && user.roles.length > 0 
                         ? user.roles.map((role, index) => (
                             <span key={index} className={styles.roleBadge}>
                               {role}
                             </span>
                           ))
                         : <span className={styles.noRole}>No role assigned</span>
                       }
                     </div>
                  </div>
                ))
              ) : (
                <div className={styles.noUsers}>No users found in this {membershipLabel}</div>
              )}
            </div>
            {viewUsersExtra}
          </div>
        </div>
      ) : activeTab.title === "Invite Users" ? (
        <div className={styles.contentColumn}>
          {!canManageUsers && (
            <div className={styles.permissionMessage}>
              {permissionMessage ?? `Your role as ${rolePermissions?.roleTitle?.toLowerCase() ?? "this user"} means you cannot use the Invite Users tab.`}
            </div>
          )}
          <div className={styles.content}>
            <div className={styles.left}>
              <InviteExistingUser
                users={clientUsersList}
                roles={roles}
                addClientUser={handleAddUser}
                areaId={areaId}
                userInfoMap={clientUsersInfoMap}
                disabled={!canManageUsers}
                onInvite={inviteExisting}
                scopeNote={inviteScopeNote}
              />
            </div>
            <div className={styles.divider}></div>
            <div className={styles.right}>
              <InviteNewUser
                roles={roles}
                addClientUser={handleAddUser}
                areaId={areaId}
                disabled={!canManageUsers}
                onInvite={inviteNew}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.contentColumn}>
          {!canManageUsers && (
            <div className={styles.permissionMessage}>
              {permissionMessage ?? `Your role as ${rolePermissions?.roleTitle?.toLowerCase() ?? "this user"} means you cannot use the Remove Users tab.`}
            </div>
          )}
          {removeUsersNote && (
            <div className={styles.removeUsersNote}>{removeUsersNote}</div>
          )}
          <div className={styles.content}>
            <div className={styles.left}>
              <RemoveUser
                users={(areaUsersList as ClientUserDisplayInfoWithRoles[]).filter((user) =>
                  !(user.roles?.length === 1 && user.roles[0] === "Owner")
                )}
                handleRemoveUserFromList={handleRemoveUserFromList}
                areaId={areaId}
                disabled={!canManageUsers}
                onRemove={removeUser}
                membershipLabel={membershipLabel}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteRemoveUsers;
